import { contentApi } from './content.api';

/**
 * The Agent Skill library, as two more endpoints on the one slice — the same cache, the
 * same envelope unwrap and the same bearer token, as `plugins.api.ts` does for its
 * own service.
 *
 * **These types are declared here and again in the service, on purpose.** The
 * service's type says what it emits and this one says what the client expects, and
 * collapsing them removes the only place a mismatch could be written down across
 * a boundary whose halves deploy independently. See the service's ADR 0002.
 */

/** A method an Agent Skill is linked to. `id` is the Confluence page id; `path` is what a link needs. */
export type AgentMethodLink = { id: string; name: string; path: string };

/**
 * What an evaluation case says, **not what a run of it produced**: the input, and
 * what a good output looks like. Nothing runs these yet, so a page that labelled
 * `expectedOutput` as "Output" would be claiming a result that does not exist.
 */
export type AgentEvaluation = {
  id: number;
  name: string;
  input: string;
  fixtures: string[];
  expectedOutput: string;
  expectations: string[];
};

/** An Agent Skill the toolkit ships, or a prompt imported from Prompt Library. */
export type AgentKind = 'agent' | 'prompt';

export type AgentVersion = {
  name: string;
  version: string;
  title: string;
  summary: string;
  prompt: string;
  methods: AgentMethodLink[];
  /** Absent on a version written before prompts existed, which were all Agent Skills. */
  kind?: AgentKind;
  source: 'repository' | 'prompt-library' | 'git';
  /** For a git source, the id of the repository it came from. */
  sourceId?: string;
  /**
   * For an Agent Skill in this repository, its file. For a prompt, its address in Prompt
   * Library. For a git source, the prompt file's path in its repository.
   */
  sourcePath: string;
  evaluations: AgentEvaluation[];
  /** Where a git source's version came from. Absent for the other sources. */
  repository?: AgentRepository;
  /** When the tag was made. ISO 8601. */
  releasedAt?: string;
  /** The tag's message. **Plain text, shown as text** and never parsed as HTML or markdown. */
  releaseNotes?: string;
  install?: AgentInstall;
};

export type AgentRepository = {
  /** The Agent Skill's directory on GitHub at the tag. */
  webUrl: string;
  cloneUrl: string;
  ref: string;
  commitSha: string;
  tag?: string;
};

/** What the install instructions are built from. */
export type AgentInstall = { skillPath: string; pluginName?: string; marketplace?: string };

/** A card: the newest version of an Agent Skill, and how many there are. */
export type AgentSummary = {
  name: string;
  title: string;
  summary: string;
  version: string;
  versionCount: number;
  methods: AgentMethodLink[];
  kind: AgentKind;
};

/**
 * What an older version costs on the first load: its number and release facts, no
 * prompt. **The rest arrives from `getAgentVersion` when the reader opens or
 * compares it**, and RTK Query caches it per version.
 */
export type AgentVersionStub = {
  version: string;
  tag?: string;
  releasedAt?: string;
  releaseNotes?: string;
};

/**
 * One Agent Skill: **the newest version in full, and every version, the newest
 * included, as a stub**, newest first.
 */
export type AgentDetail = {
  name: string;
  title: string;
  latestVersion: string;
  latest: AgentVersion;
  versions: AgentVersionStub[];
};

export const agentsApi = contentApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Every Agent Skill as a card. Small enough that the method page filters it
     * itself rather than asking for its own, the way search filters the catalog.
     */
    listAgents: builder.query<AgentSummary[], void>({
      query: () => '/content/agents',
      transformResponse: (response: { agents: AgentSummary[] }) => response.agents,
    }),

    /** One Agent Skill: the newest version in full and the rest as stubs. */
    getAgent: builder.query<AgentDetail, string>({
      query: (name) => `/content/agents/${encodeURIComponent(name)}`,
      transformResponse: (response: { agent: AgentDetail }) => response.agent,
    }),

    /**
     * One version in full, **fetched when it is opened or compared** and cached per
     * `(name, version)`, so a second look or a second comparison costs nothing. It is
     * served from the toolkit's own store, never from the repository it came from.
     */
    getAgentVersion: builder.query<AgentVersion, { name: string; version: string }>({
      query: ({ name, version }) =>
        `/content/agents/${encodeURIComponent(name)}/versions/${encodeURIComponent(version)}`,
      transformResponse: (response: { version: AgentVersion }) => response.version,
    }),
  }),
});

export const { useListAgentsQuery, useGetAgentQuery, useGetAgentVersionQuery } = agentsApi;
